import { CuffaProductionTypeEnum, CuffaProductTypeEnum } from 'common/enums';

// Use plain strings for unit types to avoid circular imports with the component file.
export interface ProdutoSelecionadoSimple {
  uuid: string;
  name: string;
  selecionado: boolean;
  expandido: boolean;
  production_area_hectares: number | null;
  volume_produced: number | null;
  volume_produced_unit: string;
  volume_commercialized: number | null;
  volume_commercialized_unit: string;
  average_commercialized_value: number | null;
  volume_self_consumption: number | null;
  volume_self_consumption_unit: string;
  volume_lost: number | null;
  volume_lost_unit: string;
  is_processed: boolean;
  production_type: CuffaProductionTypeEnum;
  product_type: CuffaProductTypeEnum;
  availability_months: any;
}

export const parseAvailabilityMonths = (availabilityMonths: any) => {
  if (typeof availabilityMonths === 'string') {
    try {
      return JSON.parse(availabilityMonths);
    } catch (e) {
      return {};
    }
  }
  return availabilityMonths ?? {};
};

export const safeParseFloat = (v: any) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

export const parseApiProductToProduto = (
  p: any,
  getAllowedUnitsFn: (name: string, productionType: string, isProcessed: boolean, productType: string) => string[]
): ProdutoSelecionadoSimple => {
  const availabilityMonths = parseAvailabilityMonths(p.available_months);

  // compute allowed units to decide default unit
  const allowed = getAllowedUnitsFn(
    p.name || p.custom_product_name || p.product_id || 'Produto',
    p.production_type,
    p.is_processed,
    p.product_type,
  );
  const defaultUnit = allowed.length === 1 ? (allowed[0]) : 'QUILOGRAMAS';

  // determine unit from API, fallback to defaultUnit
  let unidadeVolume: string = (p.measurement_unit as string) || defaultUnit;
  if (!allowed.includes(unidadeVolume)) unidadeVolume = defaultUnit;

  return {
    uuid: p.uuid,
    name: p.custom_product_name ?? p.product_id ?? 'Produto Convencional',
    selecionado: true,
    expandido: false,
    production_area_hectares: safeParseFloat(p.area_hectares),
    volume_produced: safeParseFloat(p.volume_produced),
    volume_produced_unit: unidadeVolume || defaultUnit,
    volume_commercialized: safeParseFloat(p.volume_commercialized),
    volume_commercialized_unit: unidadeVolume || defaultUnit,
    average_commercialized_value: safeParseFloat(p.commercialized_value),
    volume_self_consumption: safeParseFloat(p.volume_consumed_donated),
    volume_self_consumption_unit: unidadeVolume || defaultUnit,
    volume_lost: safeParseFloat(p.volume_lost),
    volume_lost_unit: unidadeVolume || defaultUnit,
    is_processed: Boolean(p.is_processed),
    production_type: p.production_type,
    product_type: p.product_type,
    availability_months: availabilityMonths || {},
  };
};

export const validateProdutos = (practicesConventional: boolean, produtos: ProdutoSelecionadoSimple[]) => {
  if (!practicesConventional) return { valid: true };
  const produtosSelecionados = produtos.filter(p => p.selecionado);
  if (produtosSelecionados.length === 0) {
    return { valid: false, message: 'Selecione pelo menos um produto para produção convencional.' };
  }
  for (const produto of produtosSelecionados) {
    if (!produto.production_area_hectares || produto.production_area_hectares <= 0) {
      return { valid: false, message: `Informe a área válida para ${produto.name}.` };
    }
  }
  return { valid: true };
};

export const buildConventionalApiData = (
  uuid: string,
  practicesConventional: boolean,
  produtos: ProdutoSelecionadoSimple[],
) => {
  return {
    collection_uuid: uuid,
    practices_conventional_production: practicesConventional,
    conventional_products: produtos
      .filter(p => p.selecionado)
      .map(p => ({
        product_id: p.uuid,
        name: p.name,
        production_area_hectares: p.production_area_hectares,
        volume_produced: p.volume_produced,
        volume_produced_unit: p.volume_produced_unit,
        volume_commercialized: p.volume_commercialized,
        volume_commercialized_unit: p.volume_commercialized_unit,
        average_commercialized_value: p.average_commercialized_value,
        volume_self_consumption: p.volume_self_consumption,
        volume_self_consumption_unit: p.volume_self_consumption_unit,
        volume_lost: p.volume_lost,
        volume_lost_unit: p.volume_lost_unit,
        availability_months: p.availability_months,
        is_processed: p.is_processed,
        production_type: p.production_type,
        product_type: p.product_type
      }))
  };
};
